import React, { useState, useEffect, useMemo } from 'react';
import { insightsApi } from '../api/client';

// Task 1.8: pick a reisonderdeel and see how the price moved.
//
// Two axes, and the difference between them is the whole reason this screen
// exists. By travel date you see the season: what does August cost against
// November. By horizon you see how far ahead you have to buy: what happens to
// the price between six months and two weeks before departure. The same
// points, a different question.
//
// The chart is hand-drawn SVG and not a library. This is a scatter plot with
// at most a few hundred points; pulling in a charting package for it costs
// more maintenance than it saves.

const WIDTH = 760;
const HEIGHT = 320;
const MARGIN = { left: 56, right: 16, top: 16, bottom: 40 };

const formatMoney = (b) =>
  b === null || b === undefined
    ? ''
    : `€ ${Number(b).toLocaleString('nl-BE', { maximumFractionDigits: 0 })}`;

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '';

function PriceHistory() {
  const [legs, setLegs] = useState([]);
  const [selected, setSelected] = useState('');
  const [history, setHistory] = useState(null);
  const [axis, setAxis] = useState('travel_date');
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    insightsApi.overview().then((r) => setOverview(r.data)).catch(() => {});
    insightsApi
      .legs()
      .then((r) => {
        setLegs(r.data);
        if (r.data.length > 0) setSelected(String(r.data[0].id));
      })
      .catch(() => setError('De reisonderdelen konden niet opgehaald worden.'));
  }, []);

  useEffect(() => {
    if (!selected) return;
    insightsApi
      .priceHistory(selected)
      .then((r) => setHistory(r.data))
      .catch(() => setError('Het prijsverloop kon niet opgehaald worden.'));
  }, [selected]);

  const points = useMemo(() => {
    if (!history) return [];
    return history.points
      .filter((p) => p.price_per_person !== null)
      .map((p) => ({
        ...p,
        price: Number(p.price_per_person),
        // On the travel date axis you want to see the month and not the year,
        // because the season repeats. The year becomes the colour.
        month: p.travel_date ? new Date(p.travel_date).getMonth() + 1 : null,
        year: p.travel_date ? new Date(p.travel_date).getFullYear() : null,
      }))
      .filter((p) => (axis === 'travel_date' ? p.month !== null : p.horizon_days !== null));
  }, [history, axis]);

  const years = useMemo(
    () => [...new Set(points.map((p) => p.year).filter(Boolean))].sort(),
    [points]
  );

  if (error) return <div className="message error">{error}</div>;

  return (
    <div>
      <h2>Prijsverloop per reisonderdeel</h2>
      <p className="intro">
        Waargenomen prijzen per persoon, uit de prijs-Excels. Alleen prijzen die
        Veerle op een bekende dag opzocht voor een ticket dat op die dag koopbaar
        was. Schattingen zitten er niet in.
      </p>

      {overview && overview.hotel_observations === 0 && (
        <div className="message warning">
          Hotels staan hier nog niet bij. Er zijn {overview.accommodations} accommodaties
          gekoppeld maar nul prijswaarnemingen: die komen pas wanneer de
          nachtelijke prefetch draait, en die wacht op productiesleutels van
          RateHawk. Wat je hier ziet is {overview.rail_observations} treinwaarnemingen.
        </div>
      )}

      <div className="filter-bar">
        <label>
          Reisonderdeel
          <select value={selected} onChange={(e) => setSelected(e.target.value)}>
            {legs.map((l) => (
              <option key={l.id} value={l.id}>
                {l.slug} ({l.observations})
              </option>
            ))}
          </select>
        </label>
        <label>
          Horizontale as
          <select value={axis} onChange={(e) => setAxis(e.target.value)}>
            <option value="travel_date">reisdatum, toont het seizoen</option>
            <option value="horizon">dagen vooruit, toont de horizon</option>
          </select>
        </label>
      </div>

      {history && (
        <div className="leg-header">
          <span className={`badge price-type-${history.price_type}`}>
            {history.price_type || 'prijstype onbekend'}
          </span>
          <span>{points.length} waarnemingen</span>
          {history.ceiling_price && <span>plafond {formatMoney(history.ceiling_price)}</span>}
        </div>
      )}

      {points.length === 0 ? (
        <p>Voor dit onderdeel zijn er geen bruikbare waarnemingen op deze as.</p>
      ) : (
        <Scatter points={points} axis={axis} years={years} />
      )}
    </div>
  );
}

function Scatter({ points, axis, years }) {
  const prices = points.map((p) => p.price);
  const maxPrice = Math.max(...prices);
  // The y axis starts at zero. A truncated axis turns a ten percent difference
  // into a chart that looks like a doubling.
  const yMax = Math.ceil(maxPrice / 50) * 50 || 50;

  const xValue = (p) => (axis === 'travel_date' ? p.month : p.horizon_days);
  const xValues = points.map(xValue);
  const xMin = axis === 'travel_date' ? 1 : 0;
  const xMax = axis === 'travel_date' ? 12 : Math.max(...xValues, 1);

  const plotWidth = WIDTH - MARGIN.left - MARGIN.right;
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const x = (v) => MARGIN.left + ((v - xMin) / (xMax - xMin || 1)) * plotWidth;
  const y = (v) => MARGIN.top + plotHeight - (v / yMax) * plotHeight;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(yMax * f));
  const xTicks =
    axis === 'travel_date'
      ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
      : [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(xMax * f));
  const monthNames = ['', 'jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

  // One colour per year, so a price rise across years is not read as spread
  // within the season.
  const colours = ['#1f6f8b', '#c1553b', '#4b7f52', '#8a6d3b', '#6b4e71', '#2f4858'];
  const colourOf = (year) => colours[Math.max(0, years.indexOf(year)) % colours.length];

  return (
    <div>
      <svg
        className="scatter"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label="Spreidingsdiagram van waargenomen prijzen"
      >
        {yTicks.map((v) => (
          <g key={`y${v}`}>
            <line x1={MARGIN.left} x2={WIDTH - MARGIN.right} y1={y(v)} y2={y(v)} className="grid" />
            <text x={MARGIN.left - 8} y={y(v) + 4} className="axis-label" textAnchor="end">
              {v}
            </text>
          </g>
        ))}

        {xTicks.map((v) => (
          <text key={`x${v}`} x={x(v)} y={HEIGHT - MARGIN.bottom + 18} className="axis-label" textAnchor="middle">
            {axis === 'travel_date' ? monthNames[v] : v}
          </text>
        ))}

        <text
          x={MARGIN.left + plotWidth / 2}
          y={HEIGHT - 6}
          className="axis-title"
          textAnchor="middle"
        >
          {axis === 'travel_date' ? 'maand van vertrek' : 'dagen tussen opzoeken en reizen'}
        </text>
        <text x={14} y={MARGIN.top + plotHeight / 2} className="axis-title" textAnchor="middle"
              transform={`rotate(-90 14 ${MARGIN.top + plotHeight / 2})`}>
          euro per persoon
        </text>

        {points.map((p, i) => (
          <circle
            key={i}
            cx={x(xValue(p))}
            cy={y(p.price)}
            r={4}
            fill={colourOf(p.year)}
            fillOpacity={0.65}
          >
            <title>
              {`${formatMoney(p.price)} per persoon\nreisdatum ${formatDate(p.travel_date)}`}
              {p.observed_on ? `\nopgezocht ${formatDate(p.observed_on)}` : ''}
              {p.horizon_days !== null ? `\n${p.horizon_days} dagen vooruit` : ''}
              {p.fare_class ? `\n${p.fare_class}` : ''}
            </title>
          </circle>
        ))}
      </svg>

      <div className="legend">
        {years.map((yr) => (
          <span key={yr}>
            <i style={{ background: colourOf(yr) }} /> {yr}
          </span>
        ))}
      </div>
    </div>
  );
}

export default PriceHistory;
